import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthenticationStatus, ProjectsGroupBy, ProjectsOrderBy } from '@/contracts';
import { Invitation, Profile } from '@bimdata/definitions/dto';
import { localStorage, StorageKey } from '@bimdata/client';
import { AccessLevel } from '@bimdata/definitions/const';

export const PASSWORD_RESET_WAIT_TIMEOUT = (import.meta.env.VITE_PWD_RESET_TIMEOUT) || 3600;

export type ImplicitProjectThumbnails = {
  enabled?: boolean,
  models?: number | null,
  origin?: string | null,
  small?: string | null,
  tiny?: string | null,
};

export type ModelsSortingBy = 'name' | 'author' | 'loaded' | 'revitVersion' | 'sourceSize';

export type ModelsSorting = [ ModelsSortingBy, 'A' | 'D' ];

export type MembersSortingBy = 'name' | 'access' | 'since';

export type MembersSorting = [ MembersSortingBy, 'A' | 'D' ];

export type ClientState = {
  projectsOrderBy: ProjectsOrderBy,
  projectsDescOrder: boolean,
  projectsGroupBy: ProjectsGroupBy,
  selectedModels: Record<string, number[]>;
  sidebarWidth: number;
  expandedSections: Record<string, Record<string, boolean>>;
  expandedAttributeGroups: Record<string, Record<string, boolean>>;
  expandedSettingGroups: Record<string, boolean>;
  developmentSettingsVisibility: boolean;
  settings: Record<string, any>;
  ImplicitProjectThumbnails: Record<string, ImplicitProjectThumbnails>;
  lastProjectUpdateTab: string | null,
  lastProjectUpdateModelsSorting: ModelsSorting | null,
  lastProjectUpdateMembersSorting: MembersSorting | null,
};

export type AuthenticationState = {
  status: AuthenticationStatus;
  authenticationTrace: number | null;
  claimId: string | null;
  profile: Profile | null;
  emailVerificationStarted: boolean;
  phoneVerificationStarted: boolean;
  passwordResetClaimId: string | null;
  passwordResetStarted: number | null;
  clientState: ClientState;
  invitations: Invitation[] | null;
};

function mergeClientState(prev: ClientState, next: Partial<ClientState>): ClientState {
  const settings = { ...prev.settings, ...next.settings };
  return {
    ...prev,
    ...next,
    settings,
  };
}

const initialState: AuthenticationState = {
  claimId: localStorage?.get(StorageKey.AUTHENTICATION_CLAIM_ID_KEY) || null,
  authenticationTrace: localStorage?.get('authenticationTrace') || null,
  status: AuthenticationStatus.PENDING,
  profile: null,
  emailVerificationStarted: !!localStorage.get(StorageKey.PROFILE_EMAIL_VERIFICATION_STARTED_KEY) || false,
  phoneVerificationStarted: !!localStorage.get(StorageKey.PROFILE_PHONE_VERIFICATION_STARTED_KEY) || false,
  passwordResetClaimId: localStorage?.get(StorageKey.PASSWORD_RESET_CLAIM_ID_KEY) || null,
  passwordResetStarted: localStorage?.get(StorageKey.PASSWORD_RESET_STARTED) || null,
  invitations: null,
  clientState: mergeClientState({
    projectsOrderBy: ProjectsOrderBy.NAME,
    projectsDescOrder: false,
    projectsGroupBy: ProjectsGroupBy.NONE,
    sidebarWidth: 340,
    selectedModels: {},
    expandedSections: {},
    expandedAttributeGroups: {},
    expandedSettingGroups: {},
    developmentSettingsVisibility: false,
    settings: {
      monolithLimit: 256,
      targetFramerate: 60,
      wheelAccelerationMode: 0,
      failSafe: false,
      wireframe: false,
      edges: false,
      forbidWorker: false,
      wheelInversion: false,
      enableHorizontalRotationInversion: false,
      fpvHorizontalPanInversion: false,
      fpvVerticalPanInversion: false,
      fpvHorizontalRotationInversion: false,
      fpvVerticalRotationInversion: false,
      clipIntersection: false,
    },
    ImplicitProjectThumbnails: {},
    lastProjectUpdateTab: null,
    lastProjectUpdateModelsSorting: null,
    lastProjectUpdateMembersSorting: null,
  }, localStorage?.get(StorageKey.ANON_CLIENT_STATE) || {}),
};

if (initialState.claimId) {
  initialState.status = AuthenticationStatus.PENDING_CONFIRMATION;
}

export default createSlice({
  name: 'auth',
  initialState,
  reducers: {
    sessionChecked: (state: AuthenticationState, { payload }: PayloadAction<Profile>) => {
      state.status = AuthenticationStatus.AUTHENTICATED;
      state.invitations = null;
      const { clientState, ...profile } = payload;
      state.profile = profile;

      const { settings, ...ivs } = initialState.clientState;

      state.clientState = {
        ...ivs,
        ...clientState,
        settings: {
          ...settings,
          ...clientState?.settings,
        }
      };
    },
    sessionInvalid: (state: AuthenticationState) => {
      state.status = AuthenticationStatus.UNAUTHORIZED;
      state.profile = null;
      state.invitations = null;
      state.clientState = initialState.clientState;
    },
    updateClientState: (state: AuthenticationState, { payload }: PayloadAction<Partial<ClientState>>) => {
      state.clientState = mergeClientState(state.clientState, payload);
    },
    updateProfile: (state: AuthenticationState, { payload }: PayloadAction<Profile>) => {
      const { clientState, ...profile } = payload;
      state.clientState = clientState ? mergeClientState(state.clientState, clientState as ClientState) : initialState.clientState;
      state.profile = profile;
      if (state.emailVerificationStarted && !profile.emailCandidate) {
        state.emailVerificationStarted = false;
        localStorage.remove(StorageKey.PROFILE_EMAIL_VERIFICATION_STARTED_KEY);
      }
    },
    startEmailVerification: (state: AuthenticationState) => {
      state.emailVerificationStarted = true;
      localStorage.set(StorageKey.PROFILE_EMAIL_VERIFICATION_STARTED_KEY, 'true');
    },
    finishEmailVerification: (state: AuthenticationState, { payload }: PayloadAction<string | undefined>) => {
      if (state.profile) {
        state.profile.emailConfirmed = true;
        state.profile.emailCandidate = undefined;
        if (payload) {
          state.profile.email = payload;
        }
      }
      state.emailVerificationStarted = false;
      localStorage.remove(StorageKey.PROFILE_EMAIL_VERIFICATION_STARTED_KEY);
    },
    startPhoneVerification: (state: AuthenticationState) => {
      state.phoneVerificationStarted = true;
      localStorage.set(StorageKey.PROFILE_PHONE_VERIFICATION_STARTED_KEY, 'true');
    },
    finishPhoneVerification: (state: AuthenticationState) => {
      if (state.profile) {
        state.profile.phoneConfirmed = true;
      }
      state.phoneVerificationStarted = false;
      localStorage.remove(StorageKey.PROFILE_PHONE_VERIFICATION_STARTED_KEY);
    },
    startPasswordReset: (state: AuthenticationState, { payload }: PayloadAction<string>) => {
      state.passwordResetClaimId = payload;
      state.passwordResetStarted = Date.now();
      localStorage.set(StorageKey.PASSWORD_RESET_CLAIM_ID_KEY, payload);
      localStorage.set(StorageKey.PASSWORD_RESET_STARTED, state.passwordResetStarted);
    },
    finishPasswordReset: (state: AuthenticationState) => {
      state.passwordResetClaimId = null;
      state.passwordResetStarted = null;
      localStorage.remove(StorageKey.PASSWORD_RESET_CLAIM_ID_KEY);
      localStorage.remove(StorageKey.PASSWORD_RESET_STARTED);
    },
    startLogin: (state: AuthenticationState) => {
      state.status = AuthenticationStatus.PROCESSING;
    },
    waitPhoneConfirmation: (state: AuthenticationState, { payload }: PayloadAction<string>) => {
      state.status = AuthenticationStatus.PENDING_CONFIRMATION;
      state.claimId = payload;
      localStorage.set(StorageKey.AUTHENTICATION_CLAIM_ID_KEY, payload);
    },
    startConfirmation: (state: AuthenticationState) => {
      state.status = AuthenticationStatus.CONFIRMATION;
    },
    finishLogin: (state: AuthenticationState) => {
      state.status = AuthenticationStatus.AUTHENTICATED;
      state.claimId = null;
      state.authenticationTrace = Date.now();
      localStorage.set('authenticationTrace', state.authenticationTrace);
      localStorage.remove(StorageKey.AUTHENTICATION_CLAIM_ID_KEY);
    },
    reset: (state: AuthenticationState) => {
      state.status = AuthenticationStatus.UNAUTHORIZED;
      state.claimId = null;
      state.profile = null;
      state.invitations = null;
      state.clientState = initialState.clientState;
      state.passwordResetClaimId = null;
      localStorage.remove(StorageKey.AUTHENTICATION_CLAIM_ID_KEY);
      localStorage.remove(StorageKey.PROFILE_EMAIL_VERIFICATION_STARTED_KEY);
      localStorage.remove(StorageKey.PROFILE_PHONE_VERIFICATION_STARTED_KEY);
      localStorage.remove(StorageKey.PASSWORD_RESET_CLAIM_ID_KEY);
    },
    setInvitations: (state: AuthenticationState, { payload }: PayloadAction<Invitation[]>) => {
      state.invitations = payload;
    },
    removeInvitation: (state: AuthenticationState, { payload }: PayloadAction<string>) => {
      if (state.invitations) {
        state.invitations = state.invitations.filter(({ id }) => id !== payload);
      }
    },
    addInvitation: (state: AuthenticationState, { payload }: PayloadAction<Invitation>) => {
      if (state.invitations) {
        state.invitations = [
          ...state.invitations.filter(({ id }) => id !== payload.id),
          payload,
        ];
      }
    },
    setInvitationAccessLevel: (state: AuthenticationState, { payload }: PayloadAction<{ id: string, access: AccessLevel }>) => {
      if (state.invitations) {
        const invitation = state.invitations.find(({ id }) => id === payload.id);
        if (invitation) {
          state.invitations = [
            ...state.invitations.filter(({ id }) => id !== payload.id),
            {
              ...invitation,
              project: {
                ...invitation.project,
                access: payload.access,
              }
            },
          ];
        }
      }
    }
  }
});
